import { Link } from 'react-router-dom';
import { AppStoreButton } from './AzaleaLayout';
import { AzaleaAgeCarousel } from './AzaleaAgeCarousel';
import { AzaleaPhoneMockup } from './AzaleaPhoneMockup';
import { AzaleaReviews } from './AzaleaReviews';
import { APP_NAME, APP_TAGLINE, ASSET, GALLERY_SCREENSHOTS, azaleaPath } from './constants';

const TEEN_FEELING_SAMPLE = ['happy', 'sad', 'angry', 'tired'].map((f) =>
  ASSET(`items/teen/boy/${f}.png`),
);

const FEATURES = [
  {
    title: 'Custom photo buttons',
    body: 'Parents add their own photos, like their dog, their lunch, or Grandma, and Azalea turns them into speak buttons. Background cleanup is built in.',
    images: [ASSET('screenshots/IMG_4454.PNG')],
    screenshot: true,
  },
  {
    title: 'Morning, daytime, bedtime routines',
    body: 'Routine screens group the right buttons for school mornings, after school, and wind down.',
    routines: true,
    reverse: true,
  },
  {
    title: 'Everyday categories',
    body: 'Food and drink, bathroom breaks, help, care, and fun things. Real daily life, not just one topic.',
    categoryTiles: [
      { src: ASSET('items/apple.png'), label: 'Food & drink' },
      { src: ASSET('items/brush-teeth.png'), label: 'Care & hygiene' },
      { src: ASSET('items/help.png'), label: 'Help & needs' },
      { src: ASSET('items/book.png'), label: 'Fun things' },
    ],
  },
  {
    title: 'Build real sentences',
    body: 'Tap several buttons and Azalea speaks one natural sentence, like "I want juice and a snack."',
    images: [ASSET('screenshots/IMG_4452.PNG')],
    screenshot: true,
    reverse: true,
  },
  {
    title: 'Feeling faces that match them',
    body: 'For teens and adults: the same character across emotions (happy, sad, angry, overwhelmed), not random clip art.',
    feelings: true,
  },
  {
    title: 'Grows with them',
    body: 'Kids, teens, and adults get different phrases and pictures. A teen gets "I need space." An adult gets "I need to reschedule." Not one generic board for everyone.',
    images: [
      ASSET('screenshots/IMG_4455-2.PNG'),
      ASSET('screenshots/hero-adults-feelings.png'),
    ],
    screenshot: true,
    reverse: true,
  },
  {
    title: 'Smart favorites',
    body: 'The buttons they use most rise to the top automatically. Less searching, more communicating.',
    images: [ASSET('screenshots/IMG_4445.PNG')],
    screenshot: true,
  },
  {
    title: 'Calm down Play Zone',
    body: 'Colors and bubbles when they need a sensory break, not just more buttons.',
    images: [ASSET('screenshots/IMG_4446.PNG')],
    screenshot: true,
    reverse: true,
  },
  {
    title: 'Caregiver control',
    body: 'Parent PIN, voice picker, speech speed, and an optional "fewer choices" mode for less overwhelm.',
    images: [ASSET('screenshots/IMG_4453.PNG')],
    screenshot: true,
  },
];

const FAQ_TEASER = [
  {
    q: 'Does Azalea need the internet?',
    a: 'No. Communication, voices, routines, and favorites all work offline after install.',
  },
  {
    q: 'Is Azalea free?',
    a: 'Free to communicate, forever. No subscription required for everyday communication.',
  },
  {
    q: 'Who is Azalea for?',
    a: 'Anyone who benefits from picture based communication: kids, teens, and adults. Especially helpful for people who are non speaking or find speech hard in the moment.',
  },
  {
    q: 'Is Azalea a medical device?',
    a: 'No. It is a communication support tool, not therapy or a diagnosis.',
  },
];

export function AzaleaLandingPage() {
  return (
    <main className="azalea-main">
      {/* Hero */}
      <section className="azalea-hero" aria-labelledby="azalea-hero-title">
        <div className="azalea-hero-content">
          <img
            className="azalea-hero-logo"
            src={ASSET('azalea.png')}
            alt={`${APP_NAME} logo`}
            width={380}
            height={380}
          />
          <h1 id="azalea-hero-title">
            {APP_TAGLINE}
            <span className="azalea-hero-accent">Picture communication, made personal.</span>
          </h1>
          <p className="azalea-hero-sub">
            Free to communicate, forever.
          </p>
          <p className="azalea-hero-desc">
            Picture based communication for kids, teens, and adults.
          </p>
          <AppStoreButton />
          <AzaleaAgeCarousel />
        </div>
      </section>

      {/* Who Azalea is for */}
      <section
        className="azalea-section azalea-section-soft azalea-section-narrow"
        aria-labelledby="azalea-who-title"
      >
        <h2 id="azalea-who-title">Who Azalea is for 🌺</h2>
        <p>
          Azalea was built with autistic communicators in mind, especially people who are
          non speaking, minimally speaking, or find speech hard in the moment.
        </p>
        <p>
          It is also great for anyone who benefits from visual communication: speech delays,
          anxiety, aphasia recovery, multilingual learners, temporary voice loss, and more.
        </p>
        <p>
          Any age. When you set up Azalea, you choose kid, teen, or adult, and the app adapts
          its phrases and layout to match.
        </p>
        <p>
          Many autistic people understand more than they can say out loud. Having a reliable way
          to express needs reduces frustration for the whole family. Azalea is one tool among
          many. Not a cure, not therapy, just communication support.
        </p>
      </section>

      {/* Features */}
      <section className="azalea-section" aria-labelledby="azalea-features-title">
        <h2 id="azalea-features-title">Why Azalea feels different</h2>
        <div className="azalea-features">
          {FEATURES.map((feature) => (
            <article
              key={feature.title}
              className={`azalea-feature${feature.reverse ? ' azalea-feature-reverse' : ''}`}
            >
              <div className="azalea-feature-text">
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </div>
              <div className="azalea-feature-visual">
                {feature.feelings && (
                  <div className="azalea-feelings-row" aria-label="Teen feeling face sample">
                    {TEEN_FEELING_SAMPLE.map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt=""
                        width={64}
                        height={64}
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}
                {feature.routines && (
                  <>
                    <img
                      className="azalea-feature-img-routine"
                      src={ASSET('routines/morning.png')}
                      alt="Morning routine screen"
                      loading="lazy"
                    />
                    <img
                      className="azalea-feature-img-routine"
                      src={ASSET('routines/daytime.png')}
                      alt="Daytime routine screen"
                      loading="lazy"
                    />
                    <img
                      className="azalea-feature-img-routine"
                      src={ASSET('routines/bedtime.png')}
                      alt="Bedtime routine screen"
                      loading="lazy"
                    />
                  </>
                )}
                {feature.categoryTiles && (
                  <div className="azalea-category-grid" aria-label="Everyday category examples">
                    {feature.categoryTiles.map((tile) => (
                      <div key={tile.label} className="azalea-category-tile">
                        <img src={tile.src} alt="" width={96} height={96} loading="lazy" />
                        <span>{tile.label}</span>
                      </div>
                    ))}
                  </div>
                )}
                {feature.images?.map((src) =>
                  feature.screenshot ? (
                    <AzaleaPhoneMockup
                      key={src}
                      src={src}
                      alt={`${feature.title} screenshot`}
                      size="medium"
                    />
                  ) : (
                    <img
                      key={src}
                      className="azalea-feature-img-single"
                      src={src}
                      alt={`${feature.title} screenshot`}
                      loading="lazy"
                    />
                  )
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Free to communicate */}
      <section
        className="azalea-section azalea-section-surface"
        aria-labelledby="azalea-free-title"
      >
        <div className="azalea-section-narrow">
          <h2 id="azalea-free-title">Free to communicate, forever</h2>
          <div className="azalea-free-box">
            <p>
              Azalea is built so everyday communication stays free. Tap pictures, speak phrases,
              use routines, and work offline without a subscription.
            </p>
            <ul>
              <li>
                <strong>No account required</strong>
              </li>
              <li>
                <strong>No ads, no data selling</strong>
              </li>
              <li>
                <strong>Works offline</strong>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Privacy teaser */}
      <section
        className="azalea-section azalea-section-narrow"
        aria-labelledby="azalea-privacy-title"
      >
        <h2 id="azalea-privacy-title">Privacy in plain English</h2>
        <div className="azalea-privacy-strip">
          <span>Data stays on the device</span>
          <span>No online profiles</span>
          <span>Parents can delete all app data anytime</span>
        </div>
        <p style={{ marginTop: 20 }}>
          <Link to={azaleaPath('privacy')} className="azalea-link">
            Read the full Privacy Policy →
          </Link>
          {' · '}
          <Link to={azaleaPath('terms')} className="azalea-link">
            Terms of Use →
          </Link>
        </p>
      </section>

      {/* Screenshots */}
      <section className="azalea-section" aria-labelledby="azalea-gallery-title">
        <h2 id="azalea-gallery-title">See Azalea in action</h2>
        <div className="azalea-gallery" role="list" aria-label="App screenshots">
          {GALLERY_SCREENSHOTS.map((shot) => (
            <div key={shot.src} className="azalea-gallery-item" role="listitem">
              <AzaleaPhoneMockup
                src={shot.src}
                alt={shot.alt}
                size="small"
              />
            </div>
          ))}
        </div>
      </section>

      <AzaleaReviews />

      {/* FAQ teaser */}
      <section
        className="azalea-section azalea-section-soft azalea-section-narrow"
        aria-labelledby="azalea-faq-title"
      >
        <h2 id="azalea-faq-title">Common questions</h2>
        <div className="azalea-faq-teaser">
          {FAQ_TEASER.map((item) => (
            <div key={item.q} className="azalea-faq-item">
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
        <Link to={azaleaPath('faq')} className="azalea-btn-secondary">
          See all FAQ →
        </Link>
      </section>

      {/* Under the hood */}
      <p className="azalea-under-hood">
        Works fully offline after install. No internet needed for daily communication.
      </p>
    </main>
  );
}
